/**
 * Gerenciador de gravação de áudio do microfone em blocos automáticos de 1,5 minuto.
 * Converte o áudio capturado para um arquivo WAV padrão de alta qualidade.
 */

export interface AudioRecorderOptions {
  onChunkReady: (blob: Blob, durationSeconds: number) => void;
  onTick?: (secondsRemaining: number) => void;
  onError?: (error: string) => void;
  onStateChange?: (recording: boolean) => void;
}

export class ContinuousAudioRecorder {
  private mediaRecorder: MediaRecorder | null = null;
  private stream: MediaStream | null = null;
  private timerId: number | null = null;
  private readonly chunkDurationSeconds = 90;
  private secondsRemaining = this.chunkDurationSeconds;
  private segmentStartedAt = 0;
  private shouldContinue = false;
  private isFinalizingSegment = false;
  private options: AudioRecorderOptions;
  private isRecording = false;

  constructor(options: AudioRecorderOptions) {
    this.options = options;
  }

  public async start(): Promise<boolean> {
    if (this.isRecording) return true;

    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('O navegador não suporta captura de microfone.');
      }

      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      this.isRecording = true;
      this.shouldContinue = true;
      this.startSegment();
      this.options.onStateChange?.(true);

      this.startTimer();
      return true;
    } catch (err: any) {
      const msg = err?.message || 'Permissão do microfone negada ou erro de hardware.';
      this.options.onError?.(msg);
      this.stop();
      return false;
    }
  }

  private startTimer() {
    this.clearTimer();
    this.timerId = window.setInterval(() => {
      this.secondsRemaining -= 1;
      this.options.onTick?.(this.secondsRemaining);

      if (this.secondsRemaining <= 0) {
        // Encerra o bloco atual e inicia o próximo imediatamente
        this.rotateChunk();
      }
    }, 1000);
  }

  private rotateChunk() {
    if (!this.mediaRecorder || this.mediaRecorder.state !== 'recording' || this.isFinalizingSegment) return;
    this.isFinalizingSegment = true;
    this.mediaRecorder.stop();
  }

  private startSegment() {
    if (!this.stream || !this.shouldContinue) return;

    const mimeType = this.getSupportedMimeType();
    const recorder = mimeType
      ? new MediaRecorder(this.stream, { mimeType })
      : new MediaRecorder(this.stream);
    const segmentChunks: Blob[] = [];
    this.mediaRecorder = recorder;
    this.segmentStartedAt = Date.now();
    this.secondsRemaining = this.chunkDurationSeconds;
    this.options.onTick?.(this.chunkDurationSeconds);

    recorder.ondataavailable = (event: BlobEvent) => {
      if (event.data.size > 0) segmentChunks.push(event.data);
    };

    recorder.onstop = () => {
      const duration = Math.max(1, Math.min(this.chunkDurationSeconds, Math.round((Date.now() - this.segmentStartedAt) / 1000)));
      const rawBlob = new Blob(segmentChunks, { type: recorder.mimeType || mimeType });
      this.isFinalizingSegment = false;

      // Um novo MediaRecorder é criado somente depois que o anterior encerrou.
      if (this.shouldContinue) {
        this.startSegment();
      } else {
        this.releaseStream();
      }

      // Fragmentos muito curtos ao pausar geralmente não contêm fala suficiente
      // e alguns navegadores ainda não finalizaram um contêiner decodificável.
      if (rawBlob.size < 4000 || duration < 3) return;
      void this.prepareChunk(rawBlob, duration);
    };

    recorder.start();
  }

  private async prepareChunk(rawBlob: Blob, duration: number) {
    try {
      const wavBlob = await this.convertToWav(rawBlob);
      if (wavBlob.size > 2000) this.options.onChunkReady(wavBlob, duration);
    } catch (error) {
      console.error('Erro ao converter áudio para WAV:', error);
      this.options.onError?.('Não foi possível preparar este bloco de áudio. Tente ativar a escuta novamente.');
    }
  }

  public stop() {
    this.isRecording = false;
    this.shouldContinue = false;
    this.clearTimer();

    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      try {
        this.isFinalizingSegment = true;
        this.mediaRecorder.stop();
      } catch (e) {
        console.error('Erro ao parar MediaRecorder:', e);
        this.releaseStream();
      }
    } else {
      this.releaseStream();
    }
    this.options.onStateChange?.(false);
  }

  private releaseStream() {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }
    this.mediaRecorder = null;
  }

  private clearTimer() {
    if (this.timerId !== null) {
      window.clearInterval(this.timerId);
      this.timerId = null;
    }
  }

  private getSupportedMimeType(): string {
    const types = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/mp4',
      'audio/wav',
    ];
    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }
    return '';
  }

  private async convertToWav(blob: Blob): Promise<Blob> {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const arrayBuffer = await blob.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

    const numberOfChannels = 1;
    const sampleRate = 16000;
    const sourceData = audioBuffer.getChannelData(0);
    const ratio = audioBuffer.sampleRate / sampleRate;
    const sampleCount = Math.floor(sourceData.length / ratio);
    const length = sampleCount * numberOfChannels * 2;
    const buffer = new ArrayBuffer(44 + length);
    const view = new DataView(buffer);

    // WAV Header
    this.writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + length, true);
    this.writeString(view, 8, 'WAVE');
    this.writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true); // Subchunk1Size
    view.setUint16(20, 1, true); // AudioFormat (PCM)
    view.setUint16(22, numberOfChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * numberOfChannels * 2, true);
    view.setUint16(32, numberOfChannels * 2, true);
    view.setUint16(34, 16, true); // BitsPerSample
    this.writeString(view, 36, 'data');
    view.setUint32(40, length, true);

    // Write PCM samples
    let offset = 44;
    for (let i = 0; i < sampleCount; i++) {
      const sourceIndex = Math.min(sourceData.length - 1, Math.floor(i * ratio));
      const sample = Math.max(-1, Math.min(1, sourceData[sourceIndex]));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
      offset += 2;
    }

    await audioContext.close();
    return new Blob([buffer], { type: 'audio/wav' });
  }

  private writeString(view: DataView, offset: number, string: string) {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  }
}
